import { useEffect, useMemo, useState } from 'react';

import { notifications } from '@mantine/notifications';
import { useHistory } from 'react-router-dom';

import { PROJECT_PREFIX } from '../../../const';
import { createVideo, getVideo, updateVideo } from '../../../utils/neurons';
import parseMaybeVkVideo from '../utils/parseMaybeVkVideo';
import parseMaybeYouTube from '../utils/parseMaybeYouTube';

const useVideoData = ({ id }) => {
    const history = useHistory();
    const [data, setData] = useState({
        slug: '',
        title: '',
        description: '',
        preview: '',
        games: [],
        url: '',
    });

    const fetchVideoData = async (options) => {
        try {
            const response = await getVideo(options);

            setData(response);
        } catch (error) {
            // ignore
        }
    };

    const handleChange = (property) => (eventOrValue) => {
        if (eventOrValue === null || eventOrValue === undefined) {
            return;
        }

        const value = typeof eventOrValue === 'object' && eventOrValue.target
            ? eventOrValue.target.value
            : eventOrValue;

        setData((prevState) => ({
            ...prevState,
            [property]: value,
        }));
    };

    useEffect(() => {
        if (id !== 'new') {
            fetchVideoData({ id });
        }
    }, [id]);

    const embedData = useMemo(() => {
        if (!data.url) {
            return;
        }

        const vkVideoData = parseMaybeVkVideo(data.url);

        if (vkVideoData) {
            return vkVideoData;
        }

        const youtubeVideoData = parseMaybeYouTube(data.url);

        if (youtubeVideoData) {
            return youtubeVideoData;
        }
    }, [data.url]);

    const validateData = () => {
        const errors = [];

        if (!embedData.src) {
            errors.push('Не удалось распознать ссылку на видео');
        }

        if (!data.title) {
            errors.push('Заголовок не должен быть пустым');
        }

        if (!data.title) {
            errors.push('Описание не должно быть пустым');
        }

        if (!data.preview) {
            errors.push('Изображение не должно быть пустым');
        }

        if (!data.url) {
            errors.push('Ссылка не должна быть пустой');
        }

        if (data.games.length <= 0) {
            errors.push('Список игр не должен быть пустым');
        }

        return errors;
    };

    const handleCreateVideo = async () => {
        try {
            const response = await createVideo({
                data: {
                    ...data,
                    url: embedData.src,
                },
            });

            setData(response);

            if (response.id) {
                history.replace(`${PROJECT_PREFIX}/video/${response.id}`);
            }

            notifications.show({
                title: 'Успешно',
                message: 'Материал успешно создан',
                color: 'green',
            });

            return true;
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: error.message,
                color: 'red',
            });
        }

        return false;
    };

    const handleUpdateVideo = async () => {
        try {
            const response = await updateVideo({
                id,
                data: {
                    ...data,
                    url: embedData.src,
                },
            });

            setData(response);

            notifications.show({
                title: 'Успешно',
                message: 'Материал успешно обновлен',
                color: 'green',
            });

            return true;
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: error.message,
                color: 'red',
            });
        }

        return false;
    };

    const handleSubmit = async () => {
        const errors = validateData(data);

        if (errors.length > 0) {
            errors.forEach((error) => {
                notifications.show({
                    title: 'Ошибка',
                    message: error,
                    color: 'red',
                });
            });

            return false;
        }

        if (id === 'new') {
            return handleCreateVideo();
        }

        return handleUpdateVideo();
    };

    const handleSubmitAndLeave = async () => {
        const isSuccess = await handleSubmit();

        if (isSuccess) {
            history.push(`${PROJECT_PREFIX}/video/`);
        }
    };

    return {
        data,
        embedData,
        handleChange,
        handleSubmit,
        handleSubmitAndLeave,
    };
};

export default useVideoData;
